import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";


function Profile() {
  const [userData, setUserData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [avatarModal, setAvatarModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarStatus, setAvatarStatus] = useState("");
  const [pwStatus, setPwStatus] = useState("");
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [flights, setFlights] = useState([]);
  const [rentals, setRentals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/");
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const controller = new AbortController();
    (async () => {
      try {
        setStatus("loading");
        setError(null);
        const res = await fetch("/api/profile", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("isAdmin");
          navigate("/");
          return;
        }
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `Request failed with ${res.status}`);
        }
        const payload = await res.json();
        setUserData(payload.user);
        setStatus("done");
        // fetch user's flights and rentals
        try {
          const [fRes, rRes] = await Promise.all([
            fetch('/api/flights', { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: controller.signal }),
            fetch('/api/car_rentals', { headers: token ? { Authorization: `Bearer ${token}` } : {}, signal: controller.signal }),
          ]);
          if (fRes.ok) {
            const fData = await fRes.json();
            setFlights(Array.isArray(fData) ? fData.filter(f => String(f.user_id) === String(payload.user.id)) : []);
          }
          if (rRes.ok) {
            const rData = await rRes.json();
            setRentals(Array.isArray(rData) ? rData.filter(rr => String(rr.user_id) === String(payload.user.id)) : []);
          }
        } catch (e) {
          // ignore related fetch errors
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message || "Något gick fel.");
        setStatus("error");
      }
    })();
    return () => controller.abort();
  }, [navigate, avatarStatus, pwStatus]);

  // Avatar upload
  async function handleAvatarUpload(e) {
    e.preventDefault();
    setAvatarStatus("Uploading...");
    const token = localStorage.getItem("token");
    if (!avatarFile) {
      setAvatarStatus("Please select a file");
      return;
    }
    const form = new FormData();
    form.append("file", avatarFile);
    try {
      const res = await fetch("/api/profile/avatar", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const data = await res.json();
      if (res.ok) {
        setAvatarStatus("Profile picture updated");
        setAvatarModal(false);
      } else {
        setAvatarStatus(data.msg || "Failed to update");
      }
    } catch (err) {
      setAvatarStatus("Network error");
    }
  }

  // Password change
  async function handlePasswordChange(e) {
    e.preventDefault();
    setPwStatus("Changing...");
    const token = localStorage.getItem("token");
    try {
      const res = await fetch("/api/profile/password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ old_password: pwOld, new_password: pwNew }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwStatus("Password changed");
        setPasswordModal(false);
      } else {
        setPwStatus(data.msg || "Failed to change password");
      }
    } catch (err) {
      setPwStatus("Network error");
    }
  }

  if (status === "loading") return <div className="p-8">Loading profile...</div>;
  if (status === "error") return <div className="p-8 text-red-600">Error: {error}</div>;
  if (!userData) return null;

  const nextFlight = flights.slice().sort((a,b)=> new Date(a.departure_time) - new Date(b.departure_time))[0];
  const nextRental = rentals.slice().sort((a,b)=> new Date(a.rental_date) - new Date(b.rental_date))[0];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="md:flex">
          <aside className="md:w-1/3 bg-gradient-to-b from-white to-gray-50 p-6 text-center">
            {userData.profile_pic ? (
              <img
                src={`/assets/profile-pic/${userData.profile_pic}`}
                alt={userData.name}
                className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-md mx-auto"
                onError={e => { e.currentTarget.onerror = null; e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-gray-300 flex items-center justify-center text-4xl font-bold mx-auto">{userData.name ? userData.name[0].toUpperCase() : '?'}</div>
            )}

            <h2 className="mt-4 text-xl font-semibold">{userData.name}</h2>
            <div className="text-sm text-gray-500">{userData.email}</div>

            <div className="mt-4 flex flex-col gap-2">
              <button onClick={() => setAvatarModal(true)} className="px-3 py-2 bg-indigo-600 text-white rounded">Change photo</button>
              <button onClick={() => setPasswordModal(true)} className="px-3 py-2 border rounded">Change password</button>
            </div>

            <div className="mt-6 text-sm text-gray-600">
              <div><span className="font-medium">Admin:</span> {userData.admin ? 'Yes' : 'No'}</div>
              <div className="mt-2"><span className="font-medium">Flights:</span> {flights.length}</div>
              <div><span className="font-medium">Rentals:</span> {rentals.length}</div>
            </div>
          </aside>

          <main className="md:flex-1 p-6">
            <h3 className="text-lg font-semibold mb-4">Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded">
                <div className="text-sm text-gray-500">Next flight</div>
                {nextFlight ? (
                  <div className="mt-2">
                    <div className="font-medium">{nextFlight.flight_number}</div>
                    <div className="text-sm text-gray-600">{nextFlight.departure} → {nextFlight.arrival}</div>
                    <div className="text-xs text-gray-400 mt-1">{nextFlight.departure_time ? new Date(nextFlight.departure_time).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">No upcoming flights</div>
                )}
              </div>

              <div className="p-4 border rounded">
                <div className="text-sm text-gray-500">Next rental</div>
                {nextRental ? (
                  <div className="mt-2">
                    <div className="font-medium">{nextRental.car_model}</div>
                    <div className="text-sm text-gray-600">{nextRental.store}</div>
                    <div className="text-xs text-gray-400 mt-1">{nextRental.rental_date ? new Date(nextRental.rental_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">No upcoming rentals</div>
                )}
              </div>
            </div>

            <section className="mt-6">
              <h4 className="text-md font-semibold mb-2">Your flights</h4>
              {flights.length === 0 ? (
                <div className="text-sm text-gray-500">No flights scheduled</div>
              ) : (
                <div className="space-y-2">
                  {flights.slice(0,5).map(f => (
                    <div key={f.id} className="p-3 border rounded flex items-center justify-between">
                      <div>
                        <div className="font-medium">{f.flight_number}</div>
                        <div className="text-sm text-gray-600">{f.departure} → {f.arrival}</div>
                      </div>
                      <div className="text-xs text-gray-400">{f.departure_time ? new Date(f.departure_time).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="mt-6">
              <h4 className="text-md font-semibold mb-2">Your rentals</h4>
              {rentals.length === 0 ? (
                <div className="text-sm text-gray-500">No rentals</div>
              ) : (
                <div className="space-y-2">
                  {rentals.slice(0,5).map(r => (
                    <div key={r.id} className="p-3 border rounded flex items-center justify-between">
                      <div>
                        <div className="font-medium">{r.car_model}</div>
                        <div className="text-sm text-gray-600">{r.store}</div>
                      </div>
                      <div className="text-xs text-gray-400">{r.rental_date ? new Date(r.rental_date).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </main>
        </div>
      </div>
      {/* Avatar Modal */}
      {avatarModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setAvatarModal(false)} />
          <div className="relative bg-white rounded-lg p-6 w-full max-w-md z-10">
            <h2 className="text-xl font-semibold mb-4">Change Profile Picture</h2>
            <form onSubmit={handleAvatarUpload} className="space-y-3">
              <input type="file" accept="image/*" onChange={e => setAvatarFile(e.target.files[0])} className="w-full" />
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setAvatarModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Upload</button>
              </div>
              {avatarStatus && <div className="text-sm mt-2">{avatarStatus}</div>}
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {passwordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setPasswordModal(false)} />
          <div className="relative bg-white rounded-lg p-6 w-full max-w-md z-10">
            <h2 className="text-xl font-semibold mb-4">Change Password</h2>
            <form onSubmit={handlePasswordChange} className="space-y-3">
              <input type="password" placeholder="Current password" value={pwOld} onChange={e => setPwOld(e.target.value)} className="w-full p-2 border rounded" required />
              <input type="password" placeholder="New password" value={pwNew} onChange={e => setPwNew(e.target.value)} className="w-full p-2 border rounded" required />
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={() => setPasswordModal(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded">Change</button>
              </div>
              {pwStatus && <div className="text-sm mt-2">{pwStatus}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
